'use client'

import React, { useMemo } from 'react'
import { cn } from '../../../utils/styles'
import { FileItem, DraggableFileItem } from './FileItem'
import { FileThumbnailGrid } from './FileThumbnail'
import type { FileListProps, FileWithUIState } from '../types'

export function FileList({
  files,
  showThumbnails = true,
  thumbnailSize = 48,
  showFileInfo = true,
  showProgress = true,
  onRemove,
  onRetry,
  className,
  variant = 'default'
}: FileListProps) {
  if (files.length === 0) {
    return null
  }

  if (variant === 'compact') {
    return (
      <FileListCompact
        files={files}
        onRemove={onRemove}
        onRetry={onRetry}
        className={className}
      />
    )
  }

  if (variant === 'gallery') {
    return (
      <FileListGallery
        files={files}
        thumbnailSize={thumbnailSize}
        onRemove={onRemove}
        onRetry={onRetry}
        className={className}
      />
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      {files.map((file, index) => (
        <FileItem
          key={file.id}
          file={file}
          index={index}
          showThumbnail={showThumbnails}
          thumbnailSize={thumbnailSize}
          showFileInfo={showFileInfo}
          showProgress={showProgress}
          onRemove={onRemove}
          onRetry={onRetry}
          variant="default"
        />
      ))}
    </div>
  )
}

// Compact variant for smaller spaces
function FileListCompact({
  files,
  onRemove,
  onRetry,
  className
}: {
  files: FileWithUIState[]
  onRemove?: (file: FileWithUIState, index: number) => void
  onRetry?: (file: FileWithUIState, index: number) => void
  className?: string
}) {
  return (
    <div className={cn('space-y-1', className)}>
      {files.map((file, index) => (
        <FileItem
          key={file.id}
          file={file}
          index={index}
          onRemove={onRemove}
          onRetry={onRetry}
          variant="compact"
        />
      ))}
    </div>
  )
}

// Gallery variant for image-focused layouts
function FileListGallery({
  files,
  thumbnailSize = 120,
  onRemove,
  onRetry,
  className
}: {
  files: FileWithUIState[]
  thumbnailSize?: number
  onRemove?: (file: FileWithUIState, index: number) => void
  onRetry?: (file: FileWithUIState, index: number) => void
  className?: string
}) {
  return (
    <div className={cn('grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4', className)}>
      {files.map((file, index) => (
        <FileItem
          key={file.id}
          file={file}
          index={index}
          thumbnailSize={thumbnailSize}
          onRemove={onRemove}
          onRetry={onRetry}
          variant="gallery"
        />
      ))}
    </div>
  )
}

// File list with status grouping
export interface FileListGroupedProps extends FileListProps {
  groupByStatus?: boolean
}

export function FileListGrouped({
  files,
  groupByStatus = false,
  ...props
}: FileListGroupedProps) {
  const groupedFiles = useMemo(() => {
    if (!groupByStatus) {
      return { all: files }
    }

    return files.reduce((groups, file) => {
      const status = file.status
      if (!groups[status]) {
        groups[status] = []
      }
      groups[status].push(file)
      return groups
    }, {} as Record<string, FileWithUIState[]>)
  }, [files, groupByStatus])

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending Upload'
      case 'uploading':
        return 'Uploading'
      case 'completed':
        return 'Completed'
      case 'error':
        return 'Failed'
      default:
        return 'Files'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-gray-600'
      case 'uploading':
        return 'text-blue-600'
      case 'completed':
        return 'text-green-600'
      case 'error':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  if (!groupByStatus) {
    return <FileList files={files} {...props} />
  }

  return (
    <div className={cn('space-y-6', props.className)}>
      {Object.entries(groupedFiles).map(([status, statusFiles]) => (
        <div key={status}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={cn('text-sm font-medium', getStatusColor(status))}>
              {getStatusLabel(status)}
            </h3>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {statusFiles.length}
            </span>
          </div>

          <FileList
            {...props}
            files={statusFiles}
            className=""
          />
        </div>
      ))}
    </div>
  )
}

// File list with drag and drop reordering
export interface DraggableFileListProps extends FileListProps {
  onReorder?: (fromIndex: number, toIndex: number) => void
  dragEnabled?: boolean
}

export function DraggableFileList({
  files,
  onReorder,
  dragEnabled = true,
  ...props
}: DraggableFileListProps) {
  // This would integrate with a drag and drop library like react-beautiful-dnd
  // For now, we'll render the basic structure

  return (
    <div className={cn('space-y-2', props.className)}>
      {files.map((file, index) => (
        <DraggableFileItem
          key={file.id}
          file={file}
          index={index}
          showThumbnail={props.showThumbnails}
          thumbnailSize={props.thumbnailSize}
          showFileInfo={props.showFileInfo}
          showProgress={props.showProgress}
          onRemove={props.onRemove}
          onRetry={props.onRetry}
          variant={props.variant}
          // Drag props would come from drag library
          isDragging={false}
          dragHandleProps={dragEnabled ? { 'data-drag-handle': true } : undefined}
        />
      ))}
    </div>
  )
}

// File list with virtual scrolling for large lists
export interface VirtualFileListProps extends FileListProps {
  itemHeight?: number
  maxHeight?: number
  overscan?: number
}

export function VirtualFileList({
  files,
  itemHeight = 64,
  maxHeight = 400,
  overscan = 5,
  ...props
}: VirtualFileListProps) {
  // This would integrate with a virtual scrolling library like react-window
  // For now, we'll render a regular list with max height

  return (
    <div
      className={cn('overflow-y-auto', props.className)}
      style={{ maxHeight }}
    >
      <div className="space-y-2">
        {files.map((file, index) => (
          <FileItem
            key={file.id}
            file={file}
            index={index}
            showThumbnail={props.showThumbnails}
            thumbnailSize={props.thumbnailSize}
            showFileInfo={props.showFileInfo}
            showProgress={props.showProgress}
            onRemove={props.onRemove}
            onRetry={props.onRetry}
            variant={props.variant}
          />
        ))}
      </div>
    </div>
  )
}

// File list with search and filtering
export interface SearchableFileListProps extends FileListProps {
  searchTerm?: string
  onSearchChange?: (term: string) => void
  filters?: {
    status?: string[]
    type?: string[]
    size?: { min?: number; max?: number }
  }
  onFiltersChange?: (filters: any) => void
}

export function SearchableFileList({
  files,
  searchTerm = '',
  onSearchChange,
  filters,
  onFiltersChange,
  ...props
}: SearchableFileListProps) {
  const filteredFiles = useMemo(() => {
    let filtered = files

    // Apply search term
    if (searchTerm) {
      filtered = filtered.filter(file =>
        file.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Apply status filter
    if (filters?.status && filters.status.length > 0) {
      filtered = filtered.filter(file => filters.status!.includes(file.status))
    }

    // Apply type filter
    if (filters?.type && filters.type.length > 0) {
      filtered = filtered.filter(file =>
        filters.type!.some(type => file.type.startsWith(type))
      )
    }

    // Apply size filter
    if (filters?.size) {
      if (filters.size.min !== undefined) {
        filtered = filtered.filter(file => file.size >= filters.size!.min!)
      }
      if (filters.size.max !== undefined) {
        filtered = filtered.filter(file => file.size <= filters.size!.max!)
      }
    }

    return filtered
  }, [files, searchTerm, filters])

  return (
    <div className="space-y-4">
      {/* Search and filter controls */}
      <div className="flex items-center justify-between">
        <div className="flex-1 max-w-sm">
          <input
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="text-sm text-gray-500">
          {filteredFiles.length} of {files.length} files
        </div>
      </div>

      {/* File list */}
      <FileList
        {...props}
        files={filteredFiles}
      />

      {/* Empty state */}
      {filteredFiles.length === 0 && files.length > 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No files match your search criteria</p>
          <button
            type="button"
            onClick={() => {
              onSearchChange?.('')
              onFiltersChange?.({})
            }}
            className="text-blue-600 hover:text-blue-700 text-sm mt-2"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  )
}